import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  TrendingUp, TrendingDown, Download, Calendar, ChevronDown,
  ArrowUpRight, ArrowDownRight, Info, Building2,
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from '../../hooks/use-toast';
import { EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { LastSyncedBadge } from './LastSyncedBadge';
import { track } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

// ---------- Date presets ----------
const fmt = (d) => d.toISOString().slice(0, 10);
const datePresets = () => {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const last3 = new Date(today); last3.setMonth(today.getMonth() - 2);
  const last3Start = new Date(last3.getFullYear(), last3.getMonth(), 1);
  const last6 = new Date(today); last6.setMonth(today.getMonth() - 5);
  const last6Start = new Date(last6.getFullYear(), last6.getMonth(), 1);
  // Indian Financial Year (Apr 1 → Mar 31)
  const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31);
  const yearStart = new Date(today.getFullYear(), 0, 1);
  return [
    { key: 'this_month',  label: today.toLocaleDateString('en-US', { month: 'long' }), start: fmt(monthStart), end: fmt(monthEnd) },
    { key: 'prev_month',  label: 'Previous Month',  start: fmt(prevMonthStart), end: fmt(prevMonthEnd) },
    { key: 'last_3m',     label: 'Last 3 Months',   start: fmt(last3Start),     end: fmt(monthEnd) },
    { key: 'last_6m',     label: 'Last 6 Months',   start: fmt(last6Start),     end: fmt(monthEnd) },
    { key: 'fy',          label: `FY ${fyStartYear}-${(fyStartYear + 1).toString().slice(-2)}`, start: fmt(fyStart), end: fmt(fyEnd) },
    { key: 'calendar_y',  label: `${today.getFullYear()} (YTD)`, start: fmt(yearStart), end: fmt(today) },
    { key: 'custom',      label: 'Custom Range',    start: '', end: '' },
  ];
};

// ============================================================
// Sub-components / helpers
// ============================================================
const TYPE_LABELS = {
  rent: 'Rent', electricity: 'Electricity', water: 'Water', food: 'Food',
  maintenance: 'Maintenance', late_fine: 'Late Fine', other: 'Other',
};
const formatTypeLabel = (t) => TYPE_LABELS[t] || (t.charAt(0).toUpperCase() + t.slice(1));

const ProfitLoss = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');

  const initial = datePresets().find(p => p.key === 'this_month');
  const [range, setRange] = useState({ preset: 'this_month', start: initial.start, end: initial.end });
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const fetchPL = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: range.start, end_date: range.end };
      if (propertyId) params.property_id = propertyId;
      const r = await axios.get(`${API}/money/profit-loss`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(r.data);
      setLastSyncedAt(new Date());
      // Auto-track when a manager actually views a loss-making period —
      // useful for triggering help articles / churn-risk segments.
      if ((r.data?.net_profit || 0) < 0) {
        track('pl_negative_observed', {
          period: r.data?.period?.label,
          net_profit: r.data.net_profit,
          revenue: r.data?.revenue?.total || 0,
          expenses: r.data?.expenses?.total || 0,
        });
      }
    } catch (e) {
      toast({ title: 'Could not load P&L', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [token, range.start, range.end, propertyId]);

  const fetchProperties = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } });
      setProperties(r.data || []);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchPL(); }, [fetchPL]);

  // Auto-refresh when any payment, expense or property mutation happens
  useDataSync([EVT.PAYMENT, EVT.EXPENSE, EVT.PROPERTY], fetchPL);
  useRefetchOnFocus(fetchPL);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    // Summary sheet
    const summary = [
      { Metric: 'Period',          Value: data.period?.label || '' },
      { Metric: 'Scope',           Value: data.scope?.property_name || 'All Properties' },
      { Metric: 'Total Revenue',   Value: data.revenue?.total || 0 },
      { Metric: 'Total Expenses',  Value: data.expenses?.total || 0 },
      { Metric: 'Net Profit',      Value: data.net_profit || 0 },
      { Metric: 'Profit Margin %', Value: `${data.profit_margin_pct || 0}%` },
      { Metric: 'Deposits Held (Liability — excluded from P&L)', Value: data.deposits_held || 0 },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Summary');
    // Revenue
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        Object.entries(data.revenue?.by_type || {}).map(([type, amount]) => ({ Type: type, Amount: amount }))
      ),
      'Revenue'
    );
    // Expenses
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        Object.entries(data.expenses?.by_category || {}).map(([cat, amount]) => ({ Category: cat, Amount: amount }))
      ),
      'Expenses'
    );
    // Trend
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(data.monthly_trend || []),
      'Monthly Trend'
    );
    XLSX.writeFile(wb, `profit_loss_${data.period?.start}_${data.period?.end}.xlsx`);
  };

  const trendMax = useMemo(() => {
    if (!data?.monthly_trend?.length) return 1;
    return Math.max(1, ...data.monthly_trend.map(m => Math.max(m.revenue || 0, m.expenses || 0)));
  }, [data]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto" />
          <p className="mt-3 text-gray-600 text-sm">Loading P&L...</p>
        </div>
      </div>
    );
  }

  const r = data || {};
  const isProfit = (r.net_profit || 0) >= 0;
  const margin = r.profit_margin_pct || 0;
  const cmp = r.comparison;

  return (
    <div className="p-6 space-y-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="pl-header-title">Profit &amp; Loss</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {r.period?.label || '—'} · {r.scope?.property_name || 'All Properties'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LastSyncedBadge syncedAt={lastSyncedAt} onRefresh={fetchPL} refreshing={loading} />
          {/* Date range chip */}
          <DateRangeChip range={range} onChange={setRange} />
          {/* Property filter */}
          <PropertyChip
            value={propertyId}
            onChange={setPropertyId}
            properties={properties}
          />
          <Button
            onClick={() => { track('cta_export_clicked', { surface: 'profit_loss', period: r.period?.label, scope: r.scope?.property_name }); exportExcel(); }}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="pl-export-btn"
          >
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
        </div>
      </div>

      {/* Big KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          testId="pl-revenue"
          label="Total Revenue"
          value={r.revenue?.total || 0}
          tone="green"
          deltaPct={cmp?.revenue_delta_pct}
        />
        <KpiCard
          testId="pl-expenses"
          label="Total Expenses"
          value={r.expenses?.total || 0}
          tone="red"
          deltaPct={cmp?.expenses_delta_pct}
          deltaInverse
        />
        <KpiCard
          testId="pl-net-profit"
          label={isProfit ? 'Net Profit' : 'Net Loss'}
          value={Math.abs(r.net_profit || 0)}
          tone={isProfit ? 'green' : 'red'}
          deltaPct={cmp?.net_delta_pct}
          icon={isProfit ? TrendingUp : TrendingDown}
        />
        <KpiCard
          testId="pl-margin"
          label="Profit Margin"
          value={`${margin}%`}
          rawValue
          tone={margin >= 30 ? 'green' : margin >= 15 ? 'amber' : 'red'}
          subtitle={margin >= 30 ? 'Healthy' : margin >= 15 ? 'Moderate' : margin > 0 ? 'Tight' : 'Loss-making'}
        />
      </div>

      {/* Comparison strip */}
      {cmp && (
        <Card className="p-3 border border-gray-200 bg-blue-50/40">
          <p className="text-xs text-gray-600">
            <span className="font-semibold">vs Previous Period ({cmp.period_label}):</span>{' '}
            Revenue {inr(cmp.revenue)} · Expenses {inr(cmp.expenses)} · Net {inr(cmp.net_profit)}
          </p>
        </Card>
      )}

      {/* Breakdown grid: Revenue + Expenses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue breakdown */}
        <Card className="p-4 border border-gray-200" data-testid="pl-revenue-breakdown">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Revenue Breakdown</h3>
            <span className="text-sm font-bold text-green-700">{inr(r.revenue?.total)}</span>
          </div>
          <BreakdownList
            items={Object.entries(r.revenue?.by_type || {})
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([type, amount]) => ({ label: formatTypeLabel(type), amount }))}
            total={r.revenue?.total || 0}
            tone="green"
            emptyText="No revenue this period."
          />
        </Card>

        {/* Expense breakdown */}
        <Card className="p-4 border border-gray-200" data-testid="pl-expense-breakdown">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Expense Breakdown</h3>
            <span className="text-sm font-bold text-red-600">{inr(r.expenses?.total)}</span>
          </div>
          <BreakdownList
            items={Object.entries(r.expenses?.by_category || {})
              .sort((a, b) => b[1] - a[1])
              .map(([cat, amount]) => ({ label: cat, amount }))}
            total={r.expenses?.total || 0}
            tone="red"
            emptyText="No expenses logged this period."
          />
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card className="p-4 border border-gray-200" data-testid="pl-trend">
        <h3 className="font-semibold text-gray-900 mb-3">6-Month Trend</h3>
        {r.monthly_trend?.length ? (
          <div className="space-y-2">
            <div className="flex items-end gap-2 sm:gap-4 h-48">
              {r.monthly_trend.map((m) => {
                const rh = trendMax ? (m.revenue / trendMax) * 100 : 0;
                const eh = trendMax ? (m.expenses / trendMax) * 100 : 0;
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5 group">
                    <div className="flex items-end gap-1 w-full justify-center" style={{ height: 'calc(100% - 24px)' }}>
                      <div
                        className="bg-green-500 group-hover:bg-green-600 transition-colors w-3 sm:w-5 rounded-t"
                        style={{ height: `${rh}%`, minHeight: m.revenue > 0 ? '2px' : '0' }}
                        title={`Revenue: ${inr(m.revenue)}`}
                      />
                      <div
                        className="bg-red-400 group-hover:bg-red-500 transition-colors w-3 sm:w-5 rounded-t"
                        style={{ height: `${eh}%`, minHeight: m.expenses > 0 ? '2px' : '0' }}
                        title={`Expenses: ${inr(m.expenses)}`}
                      />
                    </div>
                    <p className="text-[10px] sm:text-xs text-gray-600 font-medium">{m.month_label}</p>
                    <p className={`text-[10px] font-bold tabular-nums ${(m.net || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {(m.net || 0) >= 0 ? '+' : ''}{inr(m.net)}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-4 pt-2 text-xs text-gray-600">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-green-500 rounded-sm" /> Revenue</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-400 rounded-sm" /> Expenses</span>
            </div>
          </div>
        ) : (
          <p className="text-center text-sm text-gray-500 py-8">No trend data yet.</p>
        )}
      </Card>

      {/* Deposits held note */}
      <Card className="p-4 border border-amber-200 bg-amber-50/40" data-testid="pl-deposits-note">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">
              Refundable Deposits Held in this period: <span className="text-amber-700">{inr(r.deposits_held)}</span>
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              Security deposits are a refundable liability — they are <strong>not</strong> counted as revenue in this P&amp;L.
              They appear here so you know your trust-fund exposure for the period.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// Sub-components
// ============================================================
const KpiCard = ({ label, value, rawValue, tone, deltaPct, deltaInverse, icon: Icon, subtitle, testId }) => {
  const colorMap = {
    green: 'text-green-600',
    red:   'text-red-600',
    amber: 'text-amber-600',
  };
  const display = rawValue ? value : inr(value);
  const showDelta = deltaPct !== undefined && deltaPct !== null;
  // Inverse: for expenses, going UP is BAD → flip arrow color
  const positive = deltaInverse ? deltaPct <= 0 : deltaPct >= 0;
  return (
    <Card className="p-4 border border-gray-200" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{label}</p>
        {Icon && <Icon size={16} className={colorMap[tone] || ''} />}
      </div>
      <p className={`text-2xl font-bold tabular-nums mt-1 ${colorMap[tone] || 'text-gray-900'}`}>
        {display}
      </p>
      {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
      {showDelta && (
        <div className={`flex items-center gap-1 text-[11px] font-medium mt-1.5 ${positive ? 'text-green-700' : 'text-red-700'}`}>
          {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {Math.abs(deltaPct)}% vs prev period
        </div>
      )}
      {deltaPct === null && (
        <p className="text-[11px] text-gray-400 mt-1.5">— vs prev period</p>
      )}
    </Card>
  );
};

const BreakdownList = ({ items, total, tone, emptyText }) => {
  if (!items.length) {
    return <p className="text-sm text-gray-400 py-6 text-center">{emptyText}</p>;
  }
  const barColor = tone === 'green' ? 'bg-green-500' : 'bg-red-400';
  return (
    <div className="space-y-2.5">
      {items.map((it, idx) => {
        const pct = total > 0 ? Math.round((it.amount / total) * 100) : 0;
        return (
          <div key={it.label || idx} data-testid={`breakdown-row-${idx}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 capitalize">{it.label}</span>
              <span className="font-semibold text-gray-900 tabular-nums">{inr(it.amount)} <span className="text-gray-400 font-normal text-xs">({pct}%)</span></span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
              <div className={`h-full ${barColor} rounded-full`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DateRangeChip = ({ range, onChange }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState({ start: range.start, end: range.end });
  const presets = datePresets();
  const current = presets.find(p => p.key === range.preset);
  const apply = (p) => {
    if (p.key === 'custom') {
      onChange({ preset: 'custom', start: custom.start, end: custom.end });
    } else {
      onChange({ preset: p.key, start: p.start, end: p.end });
    }
    track('pl_date_preset_changed', { preset: p.key });
    setOpen(false);
  };
  const applyCustom = () => {
    if (!custom.start || !custom.end) {
      toast({ title: 'Pick both start and end dates', variant: 'destructive' });
      return;
    }
    onChange({ preset: 'custom', start: custom.start, end: custom.end });
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="pl-date-range-chip"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium whitespace-nowrap"
        >
          <Calendar size={14} /> {current?.label || 'Custom'}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="end">
        <div className="max-h-72 overflow-y-auto">
          {presets.map(p => (
            <button
              key={p.key}
              onClick={() => apply(p)}
              data-testid={`pl-preset-${p.key}`}
              className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 ${
                range.preset === p.key ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {range.preset === 'custom' && (
          <div className="border-t mt-2 pt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={custom.start} onChange={(e) => setCustom(c => ({ ...c, start: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-2 py-1" data-testid="pl-custom-start" />
              <input type="date" value={custom.end} onChange={(e) => setCustom(c => ({ ...c, end: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-2 py-1" data-testid="pl-custom-end" />
            </div>
            <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700 text-white" onClick={applyCustom}>
              Apply Custom Range
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const PropertyChip = ({ value, onChange, properties }) => {
  const [open, setOpen] = useState(false);
  const current = properties.find(p => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="pl-property-chip"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium whitespace-nowrap max-w-[180px]"
        >
          <Building2 size={14} /> <span className="truncate">{current?.name || 'All Properties'}</span>
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="end">
        <button
          onClick={() => { onChange(''); setOpen(false); }}
          data-testid="pl-property-all"
          className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 ${
            !value ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
          }`}
        >
          All Properties
        </button>
        {properties.map(p => (
          <button
            key={p.id}
            onClick={() => { onChange(p.id); setOpen(false); }}
            data-testid={`pl-property-${p.id}`}
            className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 truncate ${
              value === p.id ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
            }`}
          >
            {p.name}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default ProfitLoss;
